import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import Marquee from '../../../../components/Marquee';
import theme from '../../../../styled-components/dark';
import { withOpacity } from '../../../../styled-components/dark/helpers';
import palette from '../../../../styled-components/palette';
import { DefaultMissionBackground as defaultMissionBackgroundURL } from '../../assets/img';
import { MissionCardComplexeStatuses, type MissionCardComplexeStatus } from '../../types/components';
import { MissionOperationTypes, type MissionOperationType } from '../../types/state/missions';
import MissionEnrollmentButtonContainer from '../MissionEnrollmentButton/MissionEnrollmentButtonContainer';
import MissionExpandButtonContainer from '../MissionExpandButton/MissionExpandButtonContainer';
import MissionFreebetsPotentialGainContainer from '../MissionFreebetsPotentialGain/MissionFreebetsPotentialGainContainer';
import MissionProgressBarContainer from '../MissionProgressBar/MissionProgressBarContainer';

const EmptyWrapper = styled.div`
  position: relative;

  padding-bottom: var(--not-first-padding-bottom, 0em);
  padding-top: var(--not-first-padding-top, 0em);
  width: 100%;
`;

type WrapperType = {
  $backgroundURL?: string | null;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  display: flex;
  width: 100%;

  cursor: pointer;

  overflow: hidden;
  border-radius: 1em;
  padding: 0.65em;

  ${({ $backgroundURL }) => {
    return css`
      &:before {
        content: '';
        position: absolute;
        filter: blur(15px) brightness(90%);
        background-image: url(${$backgroundURL ?? defaultMissionBackgroundURL});
        background-repeat: no-repeat;
        background-position: center center;
        background-size: 100% auto;
        top: 0;
        left: 0;

        width: 100%;
        height: 100%;
        z-index: 1;
      }
    `;
  }};

  &:after {
    content: '';
    position: absolute;
    background: ${theme.MissionBanner.backgroundGradient};
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    z-index: 2;
  }
`;

const WonCardOverlay = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  padding: 0.65em;

  z-index: 3;
  pointer-events: none;

  opacity: 1;
  background-color: ${withOpacity(palette.greenwinamax500, '54')};
`;

const ContentRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 3;
`;

const AnimatedContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const RightColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  flex: 1 0 auto;
`;

const EllipsisSeparator = styled.div`
  height: 0.25em;
  width: 0.25em;
  flex-shrink: 0;
  background-color: ${palette.white};
  border-radius: 50%;
`;

const MissionLabel = styled.div`
  white-space: nowrap;
  color: white;
  font-size: 90%;
  font-weight: bold;
  margin-right: 0.3em;
`;

const StyledFreebetsPotentialGainContainer = styled(MissionFreebetsPotentialGainContainer)`
  margin-left: 0.3em;
`;

const StyledProgressBarContainer = styled(MissionProgressBarContainer)`
  height: 1.7em;
  width: 6em;
`;

const StyledMissionEnrollmentButtonContainer = styled(MissionEnrollmentButtonContainer)`
  font-size: 85%;
`;

const StyledMissionExpandButtonContainer = styled(MissionExpandButtonContainer)`
  margin-left: 0.6em;
`;

type MissionBannerProps = {
  className?: string;
  backgroundURL?: string | null;
  missionId: number;
  title?: string;
  showProgressBar: boolean;
  operationType?: MissionOperationType;
  optInRequired: boolean;
  onClick: () => void;
  betRedirectionOnClick: () => void;
  hasFreebets: boolean;
  missionStatus: MissionCardComplexeStatus;
};

const MissionBanner = ({
  className = '',
  backgroundURL,
  missionId,
  title,
  showProgressBar,
  operationType,
  optInRequired,
  onClick,
  betRedirectionOnClick,
  hasFreebets,
  missionStatus,
}: MissionBannerProps) => {
  const shouldInstantRedirect = useMemo(
    () => operationType === MissionOperationTypes.OTHER && !optInRequired,
    [operationType, optInRequired],
  );

  const onCardClick = useCallback(() => {
    if (shouldInstantRedirect) {
      betRedirectionOnClick();
    } else {
      onClick?.();
    }
  }, [betRedirectionOnClick, onClick, shouldInstantRedirect]);

  const renderTitleSeparator = useMemo(() => hasFreebets && title && title.length > 0, [hasFreebets, title]);

  const isWon = useMemo(() => missionStatus === MissionCardComplexeStatuses.WON, [missionStatus]);

  return (
    <EmptyWrapper className={className}>
      <Wrapper $backgroundURL={backgroundURL} onClick={onCardClick}>
        {isWon ? <WonCardOverlay /> : null}
        <ContentRow>
          <Marquee>
            <AnimatedContent>
              <MissionLabel>{title}</MissionLabel>
              {renderTitleSeparator ? <EllipsisSeparator /> : null}
              <StyledFreebetsPotentialGainContainer missionId={missionId} />
            </AnimatedContent>
          </Marquee>
          <RightColumn>
            {showProgressBar ? (
              <StyledProgressBarContainer missionId={missionId} shouldTransition={false} />
            ) : (
              <StyledMissionEnrollmentButtonContainer missionId={missionId} />
            )}
            <StyledMissionExpandButtonContainer missionId={missionId} />
          </RightColumn>
        </ContentRow>
      </Wrapper>
    </EmptyWrapper>
  );
};

export default React.memo<MissionBannerProps>(MissionBanner);
